import React, { useState, useEffect } from "react";
import {
  View,
  TextAreaField,
  TextField,
  Button,
} from "@aws-amplify/ui-react";
import { FaChartLine, FaChartPie } from "react-icons/fa";
import { BsBarChartFill, BsDot } from "react-icons/bs";
import { MdOutlineClose } from "react-icons/md";
import { IoSendSharp } from "react-icons/io5";


import IconDropdown from "../../components/dropdown/IconDropdown";
import "./newquery.css";
import Querydata from "../../components/Querydata/Querydata";
import Previewfile from "../../components/reviewexcelfile/Previewfile";
import GraphResult from "../../components/GraphResult/GraphResult";
import { getGraphData, getNlGraphData, readSelectedFile } from "../../services/AxiosServices";
import { useAuth0 } from "@auth0/auth0-react";
import * as ExcelJS from 'exceljs';

const Newquery = () => {

  // Processing messages for dynamic display
  const processingMessages = [
    "Analyzing data structures...",
    "Preprocessing Excel data...",
    "Applying natural language processing to query...",
    "Determining optimal graph type...",
    "Converting table data to visualization format...",
    "Extracting relevant columns...",
    "Building visualization schema...",
    "Applying data transformations...",
    "Running correlation analysis...",
    "Identifying data patterns...",
    "Optimizing graph parameters...",
    "Processing numerical values...",
    "Evaluating categorical variables...",
    "Calculating axis scales...",
    "Formatting data labels...",
    "Generating color scheme...",
    "Validating data integrity...",
    "Interpreting query context...",
    "Selecting visualization components..."
  ];

  // Chart options with icons
  const chartOptions = [
    {
      value: "bar",
      label: "Bar Chart",
      icon: <BsBarChartFill style={{ marginRight: "8px" }} />,
    },
    {
      value: "line",
      label: "Line Chart",
      icon: <FaChartLine style={{ marginRight: "8px" }} />,
    },
    {
      value: "pie",
      label: "Pie Chart",
      icon: <FaChartPie style={{ marginRight: "8px" }} />,
    },
    {
      value: "scatter",
      label: "Scatter Plot",
      icon: <BsDot style={{ marginRight: "8px" }} />,
    },
  ];

  const [selected, setSelected] = useState(chartOptions[0]);
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [submitSearchQuery, setSubmitSearchQuery] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showQuerydata, setShowQuerydata] = useState(false);
  const [showPreviewfile, setShowPreviewfile] = useState(false);
  const [showFilePreview, setShowFilePreview] = useState(true);
  const [showGraphResult, setShowGraphResult] = useState(false);
  const [nlGraphData, setNlGraphData] = useState<any>(null);
  const { getAccessTokenSilently } = useAuth0();
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const [fileBlob, setFileBlob] = useState<Blob | null>(null);
  const [generateGraphImage, setGenerateGraphImage] = useState<any>(null);
  const [userQuery, setUserQuery] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [fileError, setFileError] = useState("");
  const [queryError, setQueryError] = useState("");
  const [currentProcessingMessage, setCurrentProcessingMessage] = useState("");
  const [isFileChoose, setIsFileChoose] = useState(false);

  // Debug state
  const handleChartChange = (selectedOption) => {
    setSelected(selectedOption);
  };


  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isGenerating) {
      interval = setInterval(() => {
        const randomIndex = Math.floor(Math.random() * processingMessages.length);
        setCurrentProcessingMessage(processingMessages[randomIndex]);
      }, 2000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isGenerating, processingMessages]);

  const handleSearchClick = () => {
    setShowSearchInput(true);
  };

  const handleSearchClose = () => {
    setShowSearchInput(false);
    setSearchQuery("");
    setShowQuerydata(false);
    setShowGraphResult(false);
  };


  const handleSearchSubmit = async () => {
    setSubmitSearchQuery(searchQuery.trim());

    try {
      const token = await getAccessTokenSilently();
      const data = await getNlGraphData(searchQuery, token);
      setNlGraphData(data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setShowQuerydata(true);
    setShowPreviewfile(false);
    setSelectedFile(null);
    setShowFilePreview(false);
    setShowSearchInput(false);
    setSearchQuery("");
  };


  const handleSearchInputChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleQueryInputChange = (e) => {
    setUserQuery(e.target.value);
    // Clear query error when user starts typing
    if (queryError) {
      setQueryError("");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSearchSubmit();
    }
  };

  const handleFileUpload = async () => {
    document.getElementById("file-input")?.click();
  };

  const handleFileSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      const file = selectedFiles[0];
      setSelectedFile(file);
      setSelectedFileName(file.name);
      setIsFileChoose(false);
      if (fileError) {
        setFileError("");
      }
      setShowPreviewfile(false);
      setShowQuerydata(false);
      setShowFilePreview(true);
      try {
        const buffer = await file.arrayBuffer();
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer);
        const worksheet = workbook.getWorksheet(1);
        const jsonData: any[] = [];
        if (worksheet) {
          worksheet.eachRow({ includeEmpty: true }, (row) => {
            const obj: any = {};
            row.eachCell({ includeEmpty: true }, (cell, colIndex) => {
              const col = worksheet.columns[colIndex - 1];
              if (!col) return;
              obj[col.header as string] = cell.value;
            });
            jsonData.push(obj);
          });
        }
        setPreviewData(jsonData);
        setShowPreviewfile(true);
        setShowQuerydata(false);
        setShowFilePreview(false);
      } catch (error) {
        console.error("Error uploading file:", error);
      }
    }
    event.target.value = "";
  };

  const handleGenerateClick = async () => {

    setFileError("");
    setQueryError("");
    let hasError = false;

    if (!userQuery.trim()) {
      setQueryError("Describe what graph you would like to create");
      hasError = true;
    }

    if (!selectedFileName || selectedFileName.trim() === "") {
      setFileError("We need a file to generate the graph");
      hasError = true;
    }
    if (hasError) {
      return;
    }
    setIsGenerating(true);
    setShowGraphResult(false);

    setTimeout(() => {
      const rightPanel = document.querySelector('.loader-spinner');
      if (rightPanel) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
    const file = new File([fileBlob ? fileBlob : new Blob()], selectedFileName, { type: fileBlob?.type });
    const formData = new FormData();
    formData.append('query', userQuery + " Please generate a " + selected.value);
    if(isFileChoose)
    {
      formData.append('upload_file', file);
    }
    else
    {
      formData.append('upload_file', selectedFile ? selectedFile : new Blob());
    }

    try {
      const token = await getAccessTokenSilently();
      const graphData = await getGraphData(formData, token);
      setGenerateGraphImage(graphData);

      setShowGraphResult(true);
      setShowPreviewfile(true);
      setShowQuerydata(false);
    } catch (error) {
      console.error("Error uploading file:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleChooseFile = async (fileName: string) => {
    setSelectedFileName(fileName);
    if (fileError) {
      setFileError("");
    }
    try {
      const token = await getAccessTokenSilently();
      const response = await readSelectedFile(fileName, token);
      setFileBlob(response);
      setIsFileChoose(true);
      const buffer = await response.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(buffer);
      const worksheet = workbook.getWorksheet(1);
      const json: any[] = [];
      if (worksheet) {
        worksheet.eachRow({ includeEmpty: true }, (row) => {
          const obj: any = {};
          row.eachCell({ includeEmpty: true }, (cell, colIndex) => {
            const col = worksheet.columns[colIndex - 1];
            if (!col) return;
            obj[col.header as string] = cell.value;
          });
          json.push(obj);
        });
      }
      setPreviewData(json);
      setShowPreviewfile(true);
      setShowQuerydata(false);
      setShowFilePreview(false);
    } catch (error) {
      console.error("Error reading file:", error);
    }
  };

  const handleClearQuery = () => {
    setShowQuerydata(false);
    setShowPreviewfile(false);
    setShowFilePreview(true);
    setShowGraphResult(false);
    setSubmitSearchQuery("");
    setSelectedFile(null);
    setIsGenerating(false);
    setUserQuery("");
    setSelectedFileName("");
    setFileError("");
    setQueryError("");
    setCurrentProcessingMessage("");
  };

  const handlePreviewfileClear = () => {
    setShowPreviewfile(false);
    setShowQuerydata(false);
    setShowFilePreview(true);
    setShowGraphResult(false);
    setSelectedFile(null);
    setPreviewData([]);
    setIsGenerating(false);
    setSelectedFileName("");
    setUserQuery("");
    setFileError("");
    setQueryError("");
    setCurrentProcessingMessage("");
  };

  const handleGraphCopy = async () => {
    try {
      if (generateGraphImage?.graph?.bytes_base64) {
        const base64Data = generateGraphImage.graph.bytes_base64;
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'image/png' });
        const clipboardItem = new ClipboardItem({
          'image/png': blob
        });
        await navigator.clipboard.write([clipboardItem]);
      }
    } catch (error) {
      try {
        if (generateGraphImage?.graph?.bytes_base64) {
          await navigator.clipboard.writeText(generateGraphImage.graph.bytes_base64);
        }
      } catch (fallbackError) {
        console.error("All copy methods failed:", fallbackError);
      }
    }
  };

  return (
    <>
      <View>
        <div className="query-section">
          {/* Left Panel */}
          <div className="left-panel">
            {!showSearchInput && !showQuerydata && !showPreviewfile ? (
              <>
                <h4 className="section-label-top">FILE</h4>

                <div className="file-controls">
                  <Button className="custom-btn" onClick={handleSearchClick}>Search file</Button>
                  <Button className="custom-btn" onClick={handleFileUpload}>Upload file</Button>
                </div>
              </>
            ) : showSearchInput ? (
              <div className="file-search-container">
                <div className="search-input-wrapper">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    onKeyDown={handleKeyPress}
                    placeholder="ex. Covid data spreadsheet"
                    className="search-input"
                    autoFocus
                  />
                  <button
                    onClick={handleSearchClose}
                    className="search-close-btn"
                  >
                    <MdOutlineClose size={30} />
                  </button>
                  <button
                    onClick={handleSearchSubmit}
                    className="search-submit-btn"
                    disabled={!searchQuery.trim()}
                  >
                    <IoSendSharp size={40} />
                  </button>
                </div>
              </div>
            ) : null}
            {showQuerydata && (
              <div className="querydata-wrapper">
                <Querydata onChooseFile={handleChooseFile} onClear={handleClearQuery} nlGraphData={nlGraphData} searchQuery={submitSearchQuery} fileError={fileError} isRadio={true} />
              </div>
            )}
            {showPreviewfile && (
              <div className="previewfile-wrapper">
                <Previewfile onClear={handlePreviewfileClear} previewData={previewData} selectedFileName={selectedFileName} />
              </div>
            )}
            {showFilePreview && (
              <div className="file-preview">
                <div className="file-preview-inner">
                  {selectedFile ? (
                    <>
                      <div className="loader-spinner"></div>
                      <p>{currentProcessingMessage || "Please wait while we process your request."}</p>
                    </>
                  ) : (
                    <>
                      <p className="preview-text">Preview</p>
                      <p className="preview-subtext">
                        Select a file and you will see it here.
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Hidden file input */}
            <input
              id="file-input"
              type="file"
              style={{ display: "none" }}
              onChange={handleFileSelection}
              accept=".xls,.xlsx"
            />

            <h4 className="section-label">QUERY</h4>
            {queryError && (
              <div className="error-message">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px', flexShrink: 0 }}>
                  <path d="M12 2L22 20H2L12 2Z" fill="#dc3545" />
                  <path d="M12 8V13" stroke="white" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="12" cy="17" r="1" fill="white" />
                </svg>
                {queryError}
              </div>
            )}

            <TextAreaField
              label="Query"
              labelHidden
              placeholder="eg. Show me the sales trend by region for Q1 in blue color palette"
              rows={3}
              className="query-box"
              value={userQuery}
              onChange={handleQueryInputChange}
              hasError={false}
            />

            <div className="query-section-group row-style">
              <label className="custom-label">
                <strong>Table range</strong>{" "}
                <span className="optional-text">(optional)</span>
              </label>

              <div className="inline-field">
                <label className="inline-label">Start cell:</label>
                <TextField
                  placeholder="eg. A5"
                  label="Start"
                  labelHidden
                  className="custom-input fixed-width"
                />
              </div>

              <div className="inline-field">
                <label className="inline-label">End cell:</label>
                <TextField
                  placeholder="eg. A25"
                  label="End"
                  labelHidden
                  className="custom-input fixed-width"
                />
              </div>
            </div>

            {/* Chart type selection with icons */}
            <div className="chart-select-wrapper">
              <div className="chart-select">
                <label className="custom-label">
                  <strong>Output choice</strong>{" "}
                  <span className="optional-text">(optional)</span>
                </label>
                <IconDropdown
                  options={chartOptions}
                  selected={selected}
                  onChange={handleChartChange}
                  className="chart-icon-dropdown"
                />
              </div>
            </div>

            <div className="center">
              <button
                className="generate-btn"
                onClick={handleGenerateClick}
              >
                {isGenerating ? "Generating..." : "Generate"}
              </button>
            </div>
          </div>

          {/* Right Panel */}
          <div className="right-panel">
            <div className="result-placeholder">
              <h4 className="section-label-top">RESULT</h4>
              {showGraphResult ? (
                <GraphResult copyToClipBoard={handleGraphCopy} graphData={generateGraphImage} />
              ) : isGenerating ? (
                <div className="no-data-container">
                  <div className="no-data-message">
                    <div className="loader-spinner"></div>
                    <h3>Generating your graph...</h3>
                    <p>{currentProcessingMessage || "Please wait while we process your request."}</p>
                  </div>
                </div>
              ) : (
                <div className="no-data-container">
                  <div className="no-data-message">
                    <img
                      src={`${process.env.PUBLIC_URL}/nothing_to_show yet.jpg`}
                      alt="No history"
                      className="no-data-image"
                    />
                    <h3>Nothing to show yet!</h3>
                    <p>Generate the graph and it will pop up here.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </View></>
  );
};

export default Newquery;
